import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  jsonLd?: object | object[];
}

const SeoHead = ({ title, description, keywords, canonical, ogTitle, ogDescription, jsonLd }: SeoHeadProps) => {
  useEffect(() => {
    // Title
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:url', canonical, true);
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    // Last-modified
    let lastMod = document.querySelector('meta[name="last-modified"]') as HTMLMetaElement | null;
    if (!lastMod) {
      lastMod = document.createElement('meta');
      lastMod.setAttribute('name', 'last-modified');
      document.head.appendChild(lastMod);
    }
    lastMod.setAttribute('content', new Date().toISOString().split('T')[0]);

    // JSON-LD
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      // Remove old page-specific JSON-LD (not the main one in index.html)
      document.querySelectorAll('script[data-page-jsonld]').forEach(el => el.remove());
      schemas.forEach(schema => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-jsonld', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-page-jsonld]').forEach(el => el.remove());
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, jsonLd]);

  return null;
};

export default SeoHead;
